import type { LucideProps } from "lucide-react";
import type * as React from "react";
import type { Color } from "../../lib/colors";
import type { Omittable } from "../../lib/props";
import { cn } from "../../lib/utils";
import { ICON_REGISTRY, type IconName } from "./icon-registry";

export type { IconName };

/**
 * A component `Icon` can render as its glyph.
 *
 * Every lucide icon satisfies it, and so does a hand-written SVG component —
 * a brand mark, a product glyph lucide has no equivalent for. The constraint
 * is the props `Icon` passes down (`className`, `strokeWidth`, `data-color`,
 * `aria-label`), not `forwardRef`: React 19 passes `ref` as a plain prop, so
 * requiring lucide's `ForwardRefExoticComponent` would only exclude
 * components that work.
 */
export type IconComponent = React.ComponentType<LucideProps>;

/** A registry name or an icon component — accepted anywhere an icon is
 * configured as data (column defs, bulk actions, filter defs). */
export type IconSource = IconName | IconComponent;

export type IconSize = "xs" | "sm" | "md" | "lg" | "xl" | "2xl";

// Plain size-N classes. The class string containing "size-" is load-bearing:
// The base Button/MenuItem pin descendant svgs via
// [&_svg:not([class*='size-'])]:size-4 — an explicit size-* class is the
// exemption.
const ICON_SIZE: Record<IconSize, string> = {
  xs: "size-3", sm: "size-3.5", md: "size-4",
  lg: "size-5", xl: "size-6", "2xl": "size-7",
};

// DESIGN-TUNABLE: lucide draws for 24px/stroke-2; thinner strokes below 20px
// keep fine glyph detail legible.
const ICON_STROKE: Record<IconSize, number> = {
  xs: 1.5, sm: 1.5, md: 1.75, lg: 1.75, xl: 2, "2xl": 2,
};

/**
 * Any color NAME, plus `subtle` — the one tint that is an opacity of a token
 * rather than a token, so it cannot come from `data-color`.
 */
export type IconColor = Color | "subtle";

export type IconProps = Omit<LucideProps, "size" | "color"> &
  Omittable<{ size?: IconSize; color?: IconColor }> &
  (
    | Omittable<{ name: IconName; as?: never }>
    | Omittable<{ as: IconComponent; name?: never }>
  );

export function Icon({
  name, as: AsIcon, size = "md", color, className,
  "aria-label": ariaLabel, ...props
}: IconProps): React.ReactElement {
  const Cmp = name ? (ICON_REGISTRY[name] ?? ICON_REGISTRY.circle) : AsIcon!;
  const dataColor = color === "subtle" ? undefined : color;
  return (
    <Cmp
      className={cn(
        "shrink-0",
        ICON_SIZE[size],
        color === "subtle" && "text-muted-foreground/72",
        dataColor && "text-c",
        className,
      )}
      data-color={dataColor}
      strokeWidth={ICON_STROKE[size]}
      aria-hidden={ariaLabel ? undefined : true}
      aria-label={ariaLabel}
      data-slot="icon"
      {...props}
    />
  );
}
